import {
  ESubtitleAlignment,
  ESubtitleBorderStyle,
  MediaSequenceBranchMutationCreateRequest,
  MediaSequenceBranchMutationResponse,
  MediaSequenceBranchMutationUpdateRequest,
  SubtitleProfileResponse,
} from 'api/core';
import { useGetSubtitleProfiles } from 'api/useSubtitleProfilesApi';
import { LabelWithHelperText } from 'components/Form/LabelWithHelperText';
import { EntitySelectSingle } from 'components/Select/EntitySelectSingle';
import { buildQueryStateGetAll } from 'components/Table/useTableQueryState';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { assColorToRgba } from 'utils/color-util';
import './BakeSubtitles.css';

interface BakeSubtitlesProps {
  targetBranchMutation?: MediaSequenceBranchMutationResponse;
  inDialog?: boolean;
}

export const BakeSubtitles = ({
  targetBranchMutation,
  inDialog,
}: BakeSubtitlesProps) => {
  const {
    register,
    setValue,
    formState: { disabled },
  } = useFormContext<
    | MediaSequenceBranchMutationCreateRequest
    | MediaSequenceBranchMutationUpdateRequest
  >();

  const [subtitleProfile, setSubtitleProfile] =
    useState<SubtitleProfileResponse | null>(
      targetBranchMutation?.bakeSubtitles?.subtitleProfile || null
    );

  const { data: subtitleProfiles } = useGetSubtitleProfiles(
    buildQueryStateGetAll<SubtitleProfileResponse>()
  );

  const onSubtitleProfileSelected = (
    subtitleProfile: SubtitleProfileResponse | null
  ) => {
    setSubtitleProfile(subtitleProfile);
    setValue('bakeSubtitles.subtitleProfileId', subtitleProfile?.id);
  };

  if (!subtitleProfiles) return null;

  return (
    <>
      <div className="form-control">
        <LabelWithHelperText
          label="Undertekstprofil"
          helperText="Vælg en undertekstprofil for at styre udseendet af underteksterne. Hvis du ikke vælger en, vil standardprofilen blive brugt."
          inDialog={inDialog}
        />
        <EntitySelectSingle<SubtitleProfileResponse>
          data={subtitleProfiles.data}
          renderFormat={(e) => <p>{e.name}</p>}
          onSelect={onSubtitleProfileSelected}
          initialValue={subtitleProfiles.data.find(
            (p) =>
              p.id === targetBranchMutation?.bakeSubtitles?.subtitleProfileId
          )}
          searchPropertyKey="name"
          inDialog={inDialog}
          disabled={disabled}
        />
        <input
          {...register('bakeSubtitles.subtitleProfileId', { required: false })}
          type="text"
          className="hidden"
        />
      </div>

      {subtitleProfile ? (
        <SubtitlePreview subtitleProfile={subtitleProfile} />
      ) : null}
    </>
  );
};

export const SubtitlePreview = ({
  subtitleProfile,
}: {
  subtitleProfile: SubtitleProfileResponse;
}) => {
  const foregroundColor = assColorToRgba(subtitleProfile.foregroundColor);
  let outlineColor = assColorToRgba(subtitleProfile.outlineColor);
  let backgroundColor = assColorToRgba(subtitleProfile.backgroundColor);

  if (backgroundColor.endsWith(', 0)')) {
    backgroundColor = backgroundColor.slice(0, -2) + '1)';
  }

  if (outlineColor.endsWith(', 0)')) {
    outlineColor = outlineColor.slice(0, -2) + '1)';
  }

  const textShadow =
    subtitleProfile.borderStyle === ESubtitleBorderStyle.OutlineAndDropShadow
      ? `
        -1px -1px 0 ${outlineColor},
        1px -1px 0 ${outlineColor},
        -1px 1px 0 ${outlineColor},
        1px 1px 0 ${outlineColor}`
      : subtitleProfile.borderStyle === ESubtitleBorderStyle.OpaqueBox
        ? 'none'
        : '';

  const fadeClass = subtitleProfile.fadeAnimation ? 'animate-fade-in' : '';

  return (
    <div className="relative w-full">
      <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
        <img
          src="/media-orbit/dynamic-overlay-bg1.webp"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div
          className={`absolute z-10 w-full flex flex-col items-center ${fadeClass}`}
          style={
            subtitleProfile.alignment === ESubtitleAlignment.BottomCenter
              ? { bottom: '10%' }
              : subtitleProfile.alignment === ESubtitleAlignment.Center
                ? { top: '50%', transform: 'translateY(-50%)' }
                : {}
          }
        >
          <div
            style={{
              color: foregroundColor,
              fontSize: `${subtitleProfile.fontScale}rem`,
              fontFamily: subtitleProfile.fontName,
              fontWeight: subtitleProfile.bold ? 'bold' : 'normal',
              textShadow: textShadow,
              lineHeight: 1.3,
              display: 'inline-block',
              maxWidth: '100%',
            }}
            className="flex flex-col items-center text-center"
          >
            <SubtitleLine
              text="Dette er en eksempeltekst"
              backgroundColor={backgroundColor}
              borderStyle={subtitleProfile.borderStyle}
            />
            <SubtitleLine
              text="Med undertekstprofilen anvendt"
              backgroundColor={backgroundColor}
              borderStyle={subtitleProfile.borderStyle}
            />
          </div>
        </div>
      </div>

      {/* Fade-in animation (if enabled) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 1.2s ease-in-out;
        }
      `}</style>

      <div className="text-center text-gray-500 mt-2">
        Her er en <span className="font-bold">forhåndsvisning</span> af
        undertekstprofilen. Bemærk venligst, at det endelige resultat kan
        variere.
      </div>
    </div>
  );
};

const SubtitleLine = ({
  text,
  backgroundColor,
  borderStyle,
}: {
  text: string;
  backgroundColor: string;
  borderStyle: ESubtitleBorderStyle;
}) => {
  const style: React.CSSProperties = {
    margin: 0,
    backgroundColor:
      borderStyle === ESubtitleBorderStyle.OpaqueBox
        ? backgroundColor
        : 'transparent',
    padding:
      borderStyle === ESubtitleBorderStyle.OpaqueBox ? '8px 16px' : undefined,
    borderRadius:
      borderStyle === ESubtitleBorderStyle.OpaqueBox ? '4px' : undefined,
  };

  return (
    <div className="mt-3">
      <span style={style}>{text}</span>
    </div>
  );
};
